import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/animations/variants';

interface SectionProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  className?: string;
  centered?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '7xl';
  background?: 'default' | 'surface' | 'gradient';
  padding?: 'sm' | 'md' | 'lg' | 'xl';
  id?: string;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '7xl': 'max-w-7xl',
};

const backgroundClasses = {
  default: 'bg-background',
  surface: 'bg-surface',
  gradient: 'bg-gradient-to-br from-background via-surface to-background',
};

const paddingClasses = {
  sm: 'py-8 sm:py-12',
  md: 'py-12 sm:py-16',
  lg: 'py-16 sm:py-20',
  xl: 'py-20 sm:py-24',
};

export default function Section({
  children,
  title,
  subtitle,
  className = '',
  centered = false,
  maxWidth = '7xl',
  background = 'default',
  padding = 'lg',
  id,
}: SectionProps) {
  return (
    <motion.section 
      id={id}
      className={`
        ${backgroundClasses[background]}
        ${paddingClasses[padding]}
        ${className}
      `}
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      <div className={`${maxWidthClasses[maxWidth]} mx-auto px-4 sm:px-6 lg:px-8`}>
        {(title || subtitle) && (
          <motion.div 
            className={`${centered ? 'text-center' : ''} mb-8 sm:mb-12`}
            variants={fadeUp}
          >
            {title && (
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-textPrimary mb-3 sm:mb-4">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-base sm:text-lg md:text-xl text-textSecondary max-w-3xl mx-auto">
                {subtitle}
              </p>
            )}
          </motion.div>
        )}
        
        <motion.div variants={staggerContainer}>
          {children}
        </motion.div>
      </div>
    </motion.section>
  );
}